import React from 'react';
import { useLocation } from 'react-router-dom';
import { Heart, ChevronLeft } from 'lucide-react';
import { useFavourites } from '../context/FavouritesContext';

/**
 * FavouritesFab - side-tab variant.
 *
 * Lives on the RIGHT edge of the viewport, vertically centred,
 * peeking ~6px in from the screen and sliding fully into view on
 * hover. Used to fall back to a bottom-right pill on mobile, but the
 * user wants mobile to mirror desktop exactly so it now uses the same
 * side-tab pattern on every viewport.
 *
 * Behaviour:
 *  - Hidden when count === 0
 *  - Hidden on /workspace/* routes (workspace has its own design picker)
 *  - Click → opens the FavouritesSidebar drawer
 */
const FavouritesFab = () => {
  const { count, setSidebarOpen } = useFavourites();
  const location = useLocation();
  if (count === 0) return null;
  if (location.pathname.startsWith('/workspace')) return null;

  return (
    <button
      type="button"
      onClick={() => setSidebarOpen(true)}
      aria-label={`Open shortlist (${count} plan${count === 1 ? '' : 's'} saved)`}
      data-testid="favourites-fab"
      className="
        group fixed top-1/2 -translate-y-1/2 z-30
        right-0
        flex flex-col items-center gap-1.5
        bg-[#1d1d1f] text-white
        rounded-l-2xl shadow-[-8px_0_24px_rgba(15,23,42,0.18)]
        pl-3 pr-4 py-4
        transition-[background-color,box-shadow] duration-200 ease-out
        hover:bg-[#2d2d2f]
        active:bg-[#3a3a3c]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60
      "
    >
      <ChevronLeft
        className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 transition-opacity"
        aria-hidden="true"
      />
      <Heart className="w-5 h-5 fill-red-500 text-red-500" aria-hidden="true" />
      <span className="text-sm font-semibold leading-none">{count}</span>
      <span className="text-[10px] font-medium tracking-wider text-white/70 uppercase rotate-180 [writing-mode:vertical-rl] mt-1">
        Shortlist
      </span>
    </button>
  );
};

export default FavouritesFab;
